import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { selectAuthenticatedUser } from "src/store/selectors/auth";
import { ModernSelect } from "src/components/ModernSelect/ModernSelect";
import { usePropertiesAPI } from "src/SharedCustomHooks/UsePropertiesAPI/usePropertiesAPI";
import { useBranding } from "src/branding/BrandingContextProvider";
import { FilterModalSelect } from "./FilterModalSelect";

interface SelectedSuite {
  Id: string;
  name: string;
}

interface PropertyFilterState {
  project: string;
  suite: SelectedSuite[];
  building: string;
  level: string;
}

interface PropertyFiltersProps {
  propertyFilters: PropertyFilterState;
  setPropertyFilters: (next: PropertyFilterState) => void;
}

export const PropertyFilters = ({
  propertyFilters,
  setPropertyFilters,
}: PropertyFiltersProps) => {
  const user: any = useSelector(selectAuthenticatedUser);
  const { branding } = useBranding();

  const userId: string = user?.id;
  const roleName: string = user?.roles?.[0]?.role?.name;

  const {
    properties,
    propertySuites,
    buildings,
    levels,
    fetchProperties,
    fetchPropertySuites,
    fetchBuildingForProperty,
    fetchLevelsForBuilding,
  } = usePropertiesAPI();

  const { project, suite, building, level } = propertyFilters;

  const setProject = (value: string): void => {
    setPropertyFilters({ ...propertyFilters, project: value });
  };

  const setSuite = (selected: SelectedSuite[]): void => {
    setPropertyFilters({ ...propertyFilters, suite: selected });
  };

  const setBuilding = (value: string): void => {
    setPropertyFilters({ ...propertyFilters, building: value });
  };

  const setLevel = (value: string): void => {
    setPropertyFilters({ ...propertyFilters, level: value });
  };

  useEffect(() => {
    if (!userId || !roleName) {
      return;
    }

    fetchProperties(userId, roleName);
  }, [userId, roleName]);

  useEffect(() => {
    if (!project) {
      return;
    }

    fetchBuildingForProperty(project);
    fetchPropertySuites(project, userId, roleName);
  }, [project]);

  useEffect(() => {
    if (!building) {
      return;
    }

    fetchLevelsForBuilding(building);
  }, [building]);

  const suiteOptions = project
    ? propertySuites.map((item: any) => ({
        value: item.id,
        label: item.number,
      }))
    : [];

  const selectedSuiteOption =
    suite.length > 0 ? { value: suite[0].Id, label: suite[0].name } : null;

  return (
    <>
      <FilterModalSelect
        label="Project"
        value={project}
        onChange={setProject}
        placeholderText="Select Project"
        options={properties.map((item: any) => ({
          name: item.name,
          value: item.id,
        }))}
      />

      <div className="filters-modal__label">{branding.text.suite}</div>
      <div className="filters-modal-modern-select">
        <ModernSelect
          options={suiteOptions}
          value={selectedSuiteOption}
          onChange={(option: any) =>
            setSuite(option ? [{ Id: option.value, name: option.label }] : [])
          }
          placeholder={`Select ${branding.text.suite}`}
          isDisabled={!project}
          isClearable
        />
      </div>

      <FilterModalSelect
        label="Building"
        value={building}
        onChange={setBuilding}
        placeholderText="Select Building"
        disabled={!project}
        options={buildings.map((item: any) => ({
          name: item.building,
          value: item.id,
        }))}
      />

      <FilterModalSelect
        label="Level"
        value={level}
        onChange={setLevel}
        placeholderText="Select Level"
        disabled={!building}
        options={levels.map((item: any) => ({
          name: item.level,
          value: item.id,
        }))}
      />
    </>
  );
};
